import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CTABand, usePageTitle } from "../components/Shared";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const renderContent = (html) => DOMPurify.sanitize(html || "");

const COPY = {
  fr: {
    loading: "Chargement…",
    notFoundTitle: "Article introuvable",
    notFoundText: "Cet article n'existe pas ou n'est plus publié.",
    back: "Retour au blog",
    all: "Tous les articles",
  },
  en: {
    loading: "Loading…",
    notFoundTitle: "Article not found",
    notFoundText: "This article does not exist or is no longer published.",
    back: "Back to blog",
    all: "All articles",
  },
};

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function BlogArticle() {
  const { slug } = useParams();
  const { lang } = useLang();
  const c = COPY[lang];
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageTitle(article ? article.titre : "Article");

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="grid min-h-screen place-items-center bg-ivory px-6 pt-20">
        <div className="text-center" data-testid="blog-article-notfound">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{c.notFoundTitle}</h1>
          <p className="mt-3 text-slate-500">{c.notFoundText}</p>
          <Link to="/blog" data-testid="blog-back-link" className="mt-6 inline-flex items-center gap-2 font-display font-bold text-brand">
            <ArrowLeft className="h-4 w-4" /> {c.back}
          </Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return <div className="grid min-h-screen place-items-center bg-ivory pt-20 text-sm text-slate-400">{c.loading}</div>;
  }

  return (
    <div data-testid="blog-article-page">
      <section className="relative overflow-hidden bg-brand-deep pt-32 pb-14 text-white lg:pt-40">
        <div className="pointer-events-none absolute -top-32 right-[-5%] h-96 w-96 rounded-full bg-gold/15 blur-[110px]" />
        <div className="relative mx-auto max-w-3xl px-6">
          <Link to="/blog" data-testid="blog-article-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/60 transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> {c.all}
          </Link>
          <h1 className="mt-6 font-display text-3xl font-extrabold leading-tight tracking-tight text-balance sm:text-4xl lg:text-5xl">
            {article.titre}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-white/50">
            <span className="flex items-center gap-2"><User className="h-4 w-4 text-gold" /> {article.auteur_nom}</span>
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gold" /> {formatDate(article.created_at, lang)}</span>
          </div>
        </div>
      </section>
      {article.cover_url && (
        <div className="mx-auto -mt-2 max-w-4xl px-6">
          <Reveal>
            <img src={`${BACKEND}${article.cover_url}`} alt={article.titre} loading="lazy" className="-mt-8 w-full rounded-[2rem] object-cover shadow-2xl lg:max-h-[420px]" />
          </Reveal>
        </div>
      )}
      <section className="bg-ivory py-16">
        <Reveal className="mx-auto max-w-3xl px-6">
          <article
            data-testid="blog-article-content"
            className="blog-content text-base leading-relaxed text-slate-700 [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h3]:mt-8 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-slate-900 [&_p]:mt-5 [&_ul]:mt-5 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_strong]:font-bold [&_strong]:text-slate-900"
            dangerouslySetInnerHTML={{ __html: renderContent(article.contenu) }}
          />
        </Reveal>
      </section>
      <CTABand />
    </div>
  );
}
